import {Cents} from "./transaction"
import {Address, createResource, getFile, getResource, getResourceAsArrayBuffer} from "./common"
import {CheckImageSide} from "./checkDeposits"

type Account = {
	type: string
	id: string
}

type Customer = {
	type: string
	id: string
}

type Transaction = {
	type: string
	id: number
}

type Org = {
	type: "org"
	id: string
}

export enum CheckImageVersion {
	Printed = "printed",
	Deposited = "deposited",
}

interface CheckPaymentImageGetParam {
	accessToken: string
	id: string
	side: CheckImageSide.Front | CheckImageSide.Back
	version: CheckImageVersion
}

export function normalizeCheckPaymentImage(image: ArrayBuffer): ArrayBuffer | null {
	return image.byteLength > 0 ? image : null
}

export enum CheckPaymentsStatus {
	PendingReview = "PendingReview",
	MarkedForReturn = "MarkedForReturn",
	Processed = "Processed",
	Returned = "Returned",
	New = "New",
	Pending = "Pending",
	InDelivery = "InDelivery",
	ReturnedToSender = "ReturnedToSender",
	Canceled = "Canceled",
	Delivered = "Delivered",
	InProduction = "InProduction",
	PendingCancellation = "PendingCancellation",
	Rejected = "Rejected",
}

export enum CheckPaymentDeliveryStatus {
	Mailed = "Mailed",
	InLocalArea = "InLocalArea",
	Rerouted = "Rerouted",
}

export type CheckOriginationCounterparty = {
	name: string
	address: Address
	counterpartyMoved?: boolean
}

export enum CheckPaymentAdditionalVerificationStatus {
	"Required" = "Required",
	"NotRequired" = "NotRequired",
	"Approved" = "Approved",
}

export type CheckPayment = {
	type: "checkPayment"
	id: string
	attributes: {
		createdAt: Date
		updatedAt: Date
		sendAt?: Date
		amount: Cents
		status: CheckPaymentsStatus
		tags?: {[key: string]: string}
		description?: string
		returnReason?: string
		returnStatusReason?: string
		pendingReviewReasons?: string[]
		checkNumber?: string
		onUsAuxiliary?: string
		onUs?: string
		counterpartyRoutingNumber?: string
		counterpartyAccountNumber?: string
		payeeName?: string
		returnCutoffTime?: Date
		additionalVerificationStatus: CheckPaymentAdditionalVerificationStatus
		memo?: string
		expectedDelivery?: Date
		deliveryStatus?: CheckPaymentDeliveryStatus
		trackedAt?: Date
		postalCode?: string
		counterparty?: CheckOriginationCounterparty
		rejectReason?: string
		originated?: boolean
		isAccountClosureCheck?: boolean
		decisionReason?: string
	}
	relationships: {
		account: {
			data: Account
		}
		customer: {
			data: Customer
		}
		customers?: {
			data: Customer[]
		}
		org?: {
			data: Org
		}
		transaction?: {
			data: Transaction
		}
		returnedTransaction?: {
			data: Transaction
		}
	}
}

export type NameMismatchReviewReasons = {
	namesScoreMap: {
		[key: string]: string
	}
}

export type PendingReviewReasons = Array<{
	softLimit?: {}
	nameMissMatch?: NameMismatchReviewReasons
}>

export async function findCheckPayments(
	accessToken: string,
	offset: number,
	sort: string,
	searchQuery: string,
	requestType: "json" | "csv",
	limit?: number | null,
	statusFilter?: CheckPaymentsStatus[],
	orgs?: string[],
	since?: string,
	until?: string,
	accountId?: string,
	fromAmount?: Cents | "",
	toAmount?: Cents | "",
	purpose?: string[],
	additionalVerification?: string[]
) {
	const orgFilter = orgs && orgs.length > 0 ? orgs : undefined
	const query = {
		page: {
			limit: limit,
			offset,
		},
		filter: {
			status: statusFilter,
			query: searchQuery != "" ? searchQuery : null,
			orgs: orgFilter,
			since: since != "" ? since : null,
			until: until != "" ? until : null,
			accountId: accountId,
			fromAmount: fromAmount != "" ? fromAmount : null,
			toAmount: toAmount != "" ? toAmount : null,
			filterByPurpose: purpose,
			additionalVerification: additionalVerification,
		},
		sort: sort,
		include: "customer,account,org",
	}

	switch (requestType) {
		case "csv":
			return await getFile("check-payments", accessToken, query)
		default:
			return await getResource<Array<CheckPayment>>("check-payments", accessToken, query)
	}
}

export async function getCheckPayment(accessToken: string, id: string, include?: string) {
	return await getResource<CheckPayment>(`check-payments/${id}`, accessToken, {include})
}

export async function getActiveClosureCheckPayment(
	accessToken: string,
	customerId: string,
	accountId: string,
	include?: string
) {
	return await getResource<CheckPayment[]>(`check-payments/active-closure/${customerId}/${accountId}`, accessToken, {
		include,
	})
}

export async function getClosureCheckPayment(
	accessToken: string,
	customerId: string,
	accountId: string,
	include?: string
) {
	return await getResource<CheckPayment[]>(`check-payments/closure/${customerId}/${accountId}`, accessToken, {include})
}

export async function returnCheckPayment({id, accessToken, reason}: {id: string; accessToken: string; reason: string}) {
	const data = {
		type: "checkPaymentReturn",
		attributes: {
			reason: reason,
		},
	}
	return await createResource<CheckPayment>(`check-payments/${id}/return`, accessToken, data)
}

export async function cancelCheckPayment({id, accessToken}: {id: string; accessToken: string}) {
	const data = {
		type: "checkPaymentCancel",
	}
	return await createResource<CheckPayment>(`check-payments/${id}/cancel`, accessToken, data)
}

export async function approveCheckPayment({id, accessToken}: {id: string; accessToken: string}) {
	const data = {
		type: "additionalVerification",
	}
	return await createResource<CheckPayment>(`check-payments/${id}/approve`, accessToken, data)
}

export async function createClosureCheckPayment({
	accessToken,
	accountId,
	customerId,
	addressOverride,
	amount,
	tags,
	payeeName,
}: {
	accessToken: string
	accountId: string
	customerId: string
	addressOverride?: Address
	amount?: number
	tags?: Record<string, string>
	payeeName?: string
}) {
	const data = {
		type: "checkPayment",
		attributes: {
			addressOverride: addressOverride,
			...(amount !== undefined && {amount: amount}),
			tags: tags,
			...(payeeName !== undefined && {payeeName}),
		},
		relationships: {
			account: {
				data: {
					type: "account",
					id: accountId,
				},
			},
			customer: {
				data: {
					type: "customer",
					id: customerId,
				},
			},
		},
	}

	return await createResource<CheckPayment>(`check-payments/closure`, accessToken, data)
}

export async function getCheckPaymentImage(requestParam: CheckPaymentImageGetParam) {
	const side = requestParam.side === CheckImageSide.Front ? "front" : "back"
	const result = await getResourceAsArrayBuffer(
		`check-payments/${requestParam.id}/${side}/${requestParam.version}`,
		requestParam.accessToken
	)
	return result.map(normalizeCheckPaymentImage)
}
